import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  login,
  getMe,
  saveToken,
  saveRefreshToken,
  logout,
  updateAccount,
} from "@/services/auth";

import type { Permission } from "@artisancode/api-types";

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      saveToken(data.token);
      saveRefreshToken(data.refreshToken);
      // Wipe the whole cache (incl. the IndexedDB-persisted one), not just
      // "me" — otherwise a different user logging in on the same browser
      // briefly sees the previous user's cached list data before each query
      // refetches (or forever, if it errors — React Query keeps stale data
      // on error by default).
      queryClient.clear();
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return async function () {
    await logout();
    queryClient.clear();
  };
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    retry: false,
    staleTime: Infinity,
  });
}

// ponytail: reads straight from the useMe() cache — no separate permissions
// store needed, `/users/me` is already the single source of truth for it.
export function useHasPermission(permission: Permission): boolean {
  const { data } = useMe();
  return data?.permissions?.includes(permission) ?? false;
}

export { logout };
